import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { DownloadService, DownloadItem } from '../../services/download.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-download-panel',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatProgressBarModule, MatTooltipModule],
  animations: [
    trigger('slideIn', [
      state('void', style({ transform: 'translateX(-100%)', opacity: 0 })),
      state('*', style({ transform: 'translateX(0)', opacity: 1 })),
      transition('void => *', animate('300ms ease-out')),
      transition('* => void', animate('200ms ease-in'))
    ]),
    trigger('fadeOut', [
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateX(-20px)' }))
      ])
    ])
  ],
  template: `
    <div class="download-panel-container" *ngIf="downloads.length > 0">
      <div class="download-panel" @slideIn>
        <div class="panel-header">
          <mat-icon class="header-icon">download</mat-icon>
          <span class="header-title">Downloads</span>
          <button mat-icon-button class="minimize-btn" (click)="toggleMinimize()" [matTooltip]="isMinimized ? 'Expand' : 'Minimize'">
            <mat-icon>{{ isMinimized ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </div>

        <div class="panel-content" *ngIf="!isMinimized">
          <div class="download-item" *ngFor="let item of downloads; trackBy: trackById" @fadeOut>
            <div class="item-icon">
              <img [src]="item.iconPath" class="file-icon" *ngIf="item.iconPath">
              <mat-icon *ngIf="!item.iconPath">insert_drive_file</mat-icon>
            </div>

            <div class="item-details">
              <div class="item-name" [title]="item.fileName">{{ item.fileName }}</div>
              <div class="item-status">
                <span *ngIf="item.status === 'downloading'">
                  {{ item.progress }}% - {{ formatBytes(item.loaded) }} of {{ formatBytes(item.total) }}
                </span>
                <span *ngIf="item.status === 'completed'" class="status-completed">
                  <mat-icon class="status-icon">check_circle</mat-icon> Completed
                </span>
                <span *ngIf="item.status === 'error'" class="status-error">
                  <mat-icon class="status-icon">error</mat-icon> Failed
                </span>
                <span *ngIf="item.status === 'cancelled'" class="status-cancelled">
                  <mat-icon class="status-icon">cancel</mat-icon> Cancelled
                </span>
              </div>
              <mat-progress-bar
                *ngIf="item.status === 'downloading'"
                [mode]="item.progress > 0 ? 'determinate' : 'indeterminate'"
                [value]="item.progress"
                class="download-progress">
              </mat-progress-bar>
            </div>

            <div class="item-actions">
              <button mat-icon-button
                *ngIf="item.status === 'downloading'"
                (click)="cancelDownload(item.id)"
                class="cancel-btn"
                matTooltip="Cancel download">
                <mat-icon>close</mat-icon>
              </button>
              <button mat-icon-button
                *ngIf="item.status === 'completed' || item.status === 'error' || item.status === 'cancelled'"
                (click)="removeDownload(item.id)"
                class="remove-btn"
                matTooltip="Remove from list">
                <mat-icon>close</mat-icon>
              </button>
            </div>
          </div>
        </div>

        <div class="panel-footer" *ngIf="!isMinimized && downloads.length > 1">
          <button mat-button class="clear-all-btn" (click)="clearCompleted()">
            Clear completed
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .download-panel-container {
      position: fixed;
      bottom: 20px;
      left: 20px;
      z-index: 1000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .download-panel {
      background: #ffffff;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08);
      min-width: 320px;
      max-width: 380px;
      overflow: hidden;
      border: 1px solid rgba(0, 0, 0, 0.06);
    }

    .panel-header {
      display: flex;
      align-items: center;
      padding: 12px 16px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
    }

    .header-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
      margin-right: 10px;
    }

    .header-title {
      flex: 1;
      font-weight: 600;
      font-size: 14px;
      letter-spacing: 0.3px;
    }

    .minimize-btn {
      color: white;
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .minimize-btn mat-icon {
      font-size: 20px;
      width: 20px;
      height: 20px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .panel-content {
      max-height: 300px;
      overflow-y: auto;
    }

    .download-item {
      display: flex;
      align-items: flex-start;
      padding: 12px 16px;
      border-bottom: 1px solid #f0f0f0;
      transition: background-color 0.2s ease;
    }

    .download-item:last-child {
      border-bottom: none;
    }

    .download-item:hover {
      background-color: #f8f9fa;
    }

    .item-icon {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 12px;
      flex-shrink: 0;
    }

    .file-icon {
      width: 32px;
      height: 32px;
      object-fit: contain;
    }

    .item-icon mat-icon {
      font-size: 28px;
      width: 28px;
      height: 28px;
      color: #6c757d;
    }

    .item-details {
      flex: 1;
      min-width: 0;
      margin-right: 8px;
    }

    .item-name {
      font-size: 13px;
      font-weight: 500;
      color: #2d3748;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-bottom: 4px;
    }

    .item-status {
      font-size: 11px;
      color: #718096;
      display: flex;
      align-items: center;
      gap: 4px;
      margin-bottom: 6px;
    }

    .status-icon {
      font-size: 14px;
      width: 14px;
      height: 14px;
    }

    .status-completed {
      color: #38a169;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .status-error {
      color: #e53e3e;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .status-cancelled {
      color: #a0aec0;
      display: flex;
      align-items: center;
      gap: 4px;
    }

    .download-progress {
      height: 4px;
      border-radius: 2px;
    }

    ::ng-deep .download-progress .mat-progress-bar-fill::after {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }

    ::ng-deep .download-progress .mat-progress-bar-buffer {
      background-color: #e2e8f0;
    }

    .item-actions {
      display: flex;
      align-items: center;
    }

    .cancel-btn, .remove-btn {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .cancel-btn mat-icon, .remove-btn mat-icon {
      font-size: 18px;
      width: 18px;
      height: 18px;
      color: #a0aec0;
      transition: color 0.2s ease;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .cancel-btn:hover mat-icon {
      color: #e53e3e;
    }

    .remove-btn:hover mat-icon {
      color: #718096;
    }

    .panel-footer {
      padding: 8px 16px;
      background: #f8f9fa;
      border-top: 1px solid #f0f0f0;
      text-align: right;
    }

    .clear-all-btn {
      font-size: 12px;
      color: #667eea;
    }

    /* Scrollbar styling */
    .panel-content::-webkit-scrollbar {
      width: 6px;
    }

    .panel-content::-webkit-scrollbar-track {
      background: #f1f1f1;
    }

    .panel-content::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 3px;
    }

    .panel-content::-webkit-scrollbar-thumb:hover {
      background: #a1a1a1;
    }

    /* Dark mode support */
    @media (prefers-color-scheme: dark) {
      .download-panel {
        background: #1a202c;
        border-color: rgba(255, 255, 255, 0.1);
      }

      .download-item {
        border-bottom-color: #2d3748;
      }

      .download-item:hover {
        background-color: #2d3748;
      }

      .item-name {
        color: #e2e8f0;
      }

      .item-status {
        color: #a0aec0;
      }

      .panel-footer {
        background: #2d3748;
        border-top-color: #4a5568;
      }
    }
  `]
})
export class DownloadPanelComponent implements OnInit, OnDestroy {
  downloads: DownloadItem[] = [];
  isMinimized = false;
  private subscription: Subscription;

  constructor(private downloadService: DownloadService) {}

  ngOnInit() {
    this.subscription = this.downloadService.downloads$.subscribe(downloads => {
      this.downloads = downloads;
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  toggleMinimize() {
    this.isMinimized = !this.isMinimized;
  }

  cancelDownload(id: string) {
    this.downloadService.cancelDownload(id);
  }

  removeDownload(id: string) {
    this.downloadService.removeDownload(id);
  }

  clearCompleted() {
    this.downloadService.clearCompleted();
  }

  trackById(index: number, item: DownloadItem): string {
    return item.id;
  }

  formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }
}
